import { createFileRoute } from "@tanstack/react-router"
import { Typography } from "@/components/ui/typography"

export const Route = createFileRoute("/_public/privacy-policy")({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <main>
        <article>
          <header>
            <Typography as="h1" size="3xl" className="mb-4">
              Privacy Policy
            </Typography>
            <Typography as="p" size="sm" className="text-muted-foreground mb-6">
              Effective Date: <time dateTime="2026-06-05">June 5, 2026</time>
            </Typography>
          </header>

          <Typography as="p" className="mb-6">
            At Traction Technologies Inc. / 1546045 B.C. LTD. ("Legacy Keeper," "we," "us," or
            "our"), we are deeply committed to protecting your privacy. This Privacy Policy explains
            how we collect, use, protect, and handle your personal information when you use the
            Legacy Keeper service ("Service").
          </Typography>
          <Typography as="p" className="mb-8 font-semibold">
            Your privacy is paramount to us. We have designed Legacy Keeper with privacy as a core
            principle, ensuring that your estate planning information remains confidential and
            accessible only to you and those you explicitly authorize.
          </Typography>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              1. Our Privacy Commitment
            </Typography>
            <Typography as="p" className="mb-4 font-semibold">
              We want to be clear about our privacy practices from the start:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Your data stays within Legacy Keeper:</strong> We do not use, share, or
                transfer your personal information outside of the Legacy Keeper platform unless you
                explicitly authorize it through our collaboration features.
              </li>
              <li>
                <strong>We never monetize your data:</strong> We do not sell, rent, trade, or
                otherwise monetize your personal information in any way.
              </li>
              <li>
                <strong>Nothing is shared without your permission:</strong> Your data is only
                disclosed through the collaboration features you explicitly authorize, to the
                service providers strictly necessary to operate the Service, or where required by
                law.
              </li>
              <li>
                <strong>You control who sees your information:</strong> Only you and the people you
                explicitly authorize can access your data.
              </li>
              <li>
                <strong>Professional User access is controlled by you:</strong> A Professional User
                can only access your account if they create it for you or you accept an invitation
                to collaborate. You can revoke their access at any time.
              </li>
              <li>
                <strong>Our AI does not train on your identifiable data:</strong> The foundation
                models that power our AI are never trained on your data. We use only anonymized,
                de-identified information to improve our own features.
              </li>
              <li>
                <strong>Partner integrations are vetted and transparent:</strong> You or a
                Professional User can connect approved partner services (such as a CRM or planning
                tools) to Legacy Keeper. We do not offer an open or public API — every integration
                is individually vetted under a partnership agreement. If you connect a partner
                service yourself, you take responsibility for it and can revoke it directly from
                your dashboard; if a Professional User connects one, we notify you and you can ask
                for it to be revoked.
              </li>
            </ul>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              2. Information We Collect
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.1 Account Information
            </Typography>
            <Typography as="p" className="mb-4">
              When you create a Legacy Keeper account, we collect:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Name (first and last)</li>
              <li>Email address</li>
              <li>Phone number (optional)</li>
              <li>Password (encrypted and never stored in plain text)</li>
              <li>For Professional Users: professional credentials and licensing information</li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.2 Estate Planning Information
            </Typography>
            <Typography as="p" className="mb-4">
              As you use Legacy Keeper, you may provide:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Personal and family information</li>
              <li>Asset and financial information</li>
              <li>Beneficiary details</li>
              <li>Healthcare directives and preferences</li>
              <li>Documents and files you upload</li>
              <li>Any other information you choose to include in your estate plan</li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.3 Payment Information
            </Typography>
            <Typography as="p" className="mb-6">
              If you subscribe to a paid plan, we collect payment information through our secure
              third-party payment processor. We do not store complete credit card numbers on our
              servers. Our payment processor handles all payment card information in compliance with
              PCI-DSS standards.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.4 Usage Information
            </Typography>
            <Typography as="p" className="mb-4">
              We automatically collect certain technical information when you use the Service:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Device information (type, operating system, browser)</li>
              <li>IP address (for security and geographic location purposes)</li>
              <li>Login times and dates</li>
              <li>Pages visited and features used</li>
              <li>Error logs and diagnostic data</li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2.5 Cookies and Similar Technologies
            </Typography>
            <Typography as="p" className="mb-6">
              We use cookies and similar technologies for keeping you logged in, remembering your
              preferences, security and fraud prevention, and understanding how you use the Service
              to improve functionality. You can control cookies through your browser settings, but
              some features of the Service may not function properly if you disable cookies.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              3. How We Use Your Information
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.1 To Provide the Service
            </Typography>
            <Typography as="p" className="mb-6">
              We use your information to create and maintain your account, process your estate
              planning information using our AI technology, generate documents and recommendations,
              enable collaboration with the professional advisors and family members you authorize,
              store and secure your data, and provide customer support.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.2 To Improve and Protect the Service
            </Typography>
            <Typography as="p" className="mb-6">
              We use technical and usage information to monitor and improve Service performance,
              identify and fix technical problems, detect and prevent fraud, abuse, and security
              threats, understand how users interact with features, and develop new features and
              functionality.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.3 To Communicate With You
            </Typography>
            <Typography as="p" className="mb-6">
              We use your contact information to send service-related notifications, respond to your
              inquiries, notify you of changes to our terms or policies, and send subscription and
              billing information. We will not send you marketing emails unless you explicitly opt
              in, and you can unsubscribe at any time.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3.4 How AI Works With Your Data
            </Typography>
            <Typography as="p" className="mb-6">
              Legacy Keeper's AI features are powered by Amazon Bedrock.
            </Typography>
            <Typography as="p" className="mb-6 font-semibold">
              Foundation models.
            </Typography>
            <Typography as="p" className="mb-6">
              The data you provide is never used to train the underlying foundation models that
              power our AI. Your information and documents are not used to train these models, are
              not retained by our AI provider, and are not used to improve AI services for any other
              party. Each AI interaction is processed in real time within our secure AWS environment
              and is independent, so your information remains private and confidential.
            </Typography>
            <Typography as="p" className="mb-6 font-semibold">
              Our own service improvement.
            </Typography>
            <Typography as="p" className="mb-6">
              To improve our features — such as document data extraction and report generation — we
              may use anonymized, de-identified content for context building and internal model
              development. Before any such use, information is anonymized so that it can no longer
              be associated with you or any other individual, and we apply safeguards designed to
              prevent any individual's information from being reproduced. We do not use identifiable
              personal information to train or fine-tune our models. Anonymized data that cannot be
              traced back to you is no longer personal information and may be used to build and
              improve the Service.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              4. How We Protect Your Information
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.1 Encryption and Infrastructure
            </Typography>
            <Typography as="p" className="mb-4">
              We implement multiple layers of security to protect your data:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Encryption in transit:</strong> All data transmitted to and from the Service
                is encrypted using TLS (version 1.2 or higher).
              </li>
              <li>
                <strong>Encryption at rest:</strong> All stored data is encrypted using AES-256
                encryption.
              </li>
              <li>
                <strong>Secure infrastructure:</strong> Your data is hosted on Amazon Web Services
                (AWS) cloud infrastructure with enterprise-grade security controls, network
                isolation, and managed key services.
              </li>
              <li>
                <strong>Access controls:</strong> Strict authentication and authorization systems
                ensure only authorized users can access data.
              </li>
              <li>
                <strong>Data isolation:</strong> Each user's data is logically separated and
                isolated from other users.
              </li>
              <li>
                <strong>Security monitoring:</strong> Ongoing monitoring for suspicious activity and
                security threats, with regular security assessments and vulnerability testing.
              </li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.2 Backups
            </Typography>
            <Typography as="p" className="mb-6">
              We maintain automated, encrypted backups of the Service to protect against data loss,
              accidental deletion, and system failures. Backups are retained on a rolling 30-day
              cycle and are encrypted using the same standards as our active systems.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.3 Limited Access to Your Data
            </Typography>
            <Typography as="p" className="mb-6">
              Legacy Keeper employees, including our support team, do not have routine access to
              your identifiable estate planning data. Our systems are designed so that your
              sensitive information remains accessible only to you and those you explicitly
              authorize. In rare cases, you may grant temporary, logged, and audited access to a
              Legacy Keeper administrator to resolve a specific technical issue; this requires your
              explicit permission and is revoked once the issue is resolved.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4.4 Your Security Responsibilities
            </Typography>
            <Typography as="p" className="mb-6">
              You play an important role in keeping your information secure: choose a strong, unique
              password; never share it; enable multi-factor authentication; log out on shared
              devices; keep your contact information current; and report any suspicious activity
              immediately.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              5. Information Sharing and Disclosure
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.1 We Do Not Sell or Monetize Your Information
            </Typography>
            <Typography as="p" className="mb-6 font-semibold">
              We never sell, rent, trade, or otherwise monetize your personal information. Your
              personal information and estate planning data are not disclosed to third parties
              except as described in this section, and never without your permission.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.2 Sharing You Control
            </Typography>
            <Typography as="p" className="mb-4">
              You control who has access to your information through Legacy Keeper's collaboration
              features:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Professional Advisors:</strong> You can accept invitations from professional
                advisors, granting them the ability to view, input, and edit your data.
              </li>
              <li>
                <strong>Family and Friends:</strong> You can invite family members or friends to
                view specific information you choose to share.
              </li>
              <li>
                <strong>Revocable Access:</strong> You can revoke access for any authorized user at
                any time.
              </li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.3 Data Shared With Professional Users and Used Off-Platform
            </Typography>
            <Typography as="p" className="mb-6">
              When you share your data with a Professional User (such as a financial advisor,
              attorney, or accountant), they may export, download, or copy that data to use it
              outside of Legacy Keeper. Once data is taken off the Legacy Keeper platform, the
              Professional User is independently responsible for storing, securing, and handling
              that data in accordance with their own legal, regulatory, fiduciary, and professional
              obligations (which may include the Gramm-Leach-Bliley Act, SEC Regulation S-P,
              applicable Canadian privacy law, and professional conduct standards). Legacy Keeper
              has no control over, and is not responsible for, copies of your data held outside of
              our platform. We recommend you only share data with professionals you trust, and that
              you confirm how they will store and protect it.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.4 Partner Integrations (API)
            </Typography>
            <Typography as="p" className="mb-6">
              Legacy Keeper offers an API and MCP integration that can connect the Service to
              approved partner tools — such as dashboards, financial planning tools, or client
              relationship management (CRM) systems. In all cases, access is vetted, not open: we do
              not offer an open or public API that anyone can access, and every integration is
              individually reviewed and governed by a partnership agreement between Legacy Keeper
              and the partner service.
            </Typography>
            <Typography as="p" className="mb-6 font-semibold">
              Connections you make yourself.
            </Typography>
            <Typography as="p" className="mb-6">
              If you choose to connect your account directly to an approved partner service, you
              take full responsibility for that connection and for how the partner service stores,
              secures, and uses the data you share with it. Once your data is shared with a partner
              service you have connected, Legacy Keeper is not responsible for how that service
              handles it. You can revoke a connection you have made at any time, directly from your
              dashboard.
            </Typography>
            <Typography as="p" className="mb-6 font-semibold">
              Connections made by a Professional User.
            </Typography>
            <Typography as="p" className="mb-4">
              A Professional User may connect approved partner services they use to collaborate with
              you. When they do:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>The connection falls under the Professional User.</strong> A connected
                partner service acts as an extension of the Professional User who enabled it. Its
                access to your data is derived from, and limited to, that Professional User's
                access, and the Professional User is responsible for how the connected service
                stores, secures, and uses your data — the same way they are responsible for data
                they take off the Legacy Keeper platform. These partner services are connected by
                your Professional User and are not Legacy Keeper's own service providers.
              </li>
              <li>
                <strong>We will notify you.</strong> When a Professional User connects a partner
                service that can access your data, we will notify you of the new connection.
              </li>
              <li>
                <strong>You can request revocation.</strong> You can ask the Professional User to
                revoke a partner service's access at any time, and you retain the right to revoke
                the Professional User's access to your account entirely — which also ends any
                partner access derived from it.
              </li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.5 Service Providers
            </Typography>
            <Typography as="p" className="mb-6">
              We work with carefully selected service providers who help us operate the Service,
              including a cloud infrastructure provider (AWS), a payment processor, and our AI
              technology provider (Amazon Bedrock). These providers are contractually bound to
              protect your information, can only use your data to provide services to Legacy Keeper,
              cannot use your data for their own purposes, and must comply with strict security and
              privacy standards.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.6 Legal Requirements
            </Typography>
            <Typography as="p" className="mb-6">
              We may disclose your information if required by law — to comply with a valid subpoena,
              court order, or legal process; to respond to lawful government requests; to protect
              our legal rights; or to prevent imminent harm. Whenever legally permitted, we will
              notify you before disclosing your information.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5.7 Business Transfers
            </Typography>
            <Typography as="p" className="mb-6">
              If Legacy Keeper is involved in a merger, acquisition, or sale of assets, your
              information may be transferred as part of that transaction. We will notify you via
              email and prominent notice before your information is transferred and becomes subject
              to a different privacy policy, and you will have the opportunity to delete your data
              before any such transfer.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              6. Data Retention and Deletion
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.1 How Long We Keep Your Information
            </Typography>
            <Typography as="p" className="mb-4">
              We retain your information for different periods depending on the type:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Account Data:</strong> As long as your account is active.
              </li>
              <li>
                <strong>Estate Planning Data:</strong> As long as your account is active or until
                you delete it.
              </li>
              <li>
                <strong>Payment Records:</strong> For 7 years, as required by tax and accounting
                regulations.
              </li>
              <li>
                <strong>Technical Logs:</strong> For 90 days, for security and troubleshooting
                purposes.
              </li>
            </ul>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.2 Deleting Your Data
            </Typography>
            <Typography as="p" className="mb-6">
              You can delete all of your account data at any time directly from your account
              settings page. When you confirm deletion, your data is removed from our active,
              production systems promptly, and is fully purged from all backup systems within 30
              days as our backups cycle out. After this 30-day period, no copies of your deleted
              data remain on Legacy Keeper's systems.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.3 Before You Delete
            </Typography>
            <Typography as="p" className="mb-6">
              Before deleting your data, please understand that deletion is permanent and
              irreversible once complete. You should export any information you want to keep, as
              deleted data cannot be recovered after it has been purged.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6.4 Legal Retention Requirements
            </Typography>
            <Typography as="p" className="mb-6">
              In rare circumstances, we may be legally required to retain certain information even
              after you delete your account (such as to comply with legal holds, court orders, or
              regulatory requirements). If this applies, we will notify you and retain only the
              minimum information necessary to comply with our legal obligations, and we will delete
              it as soon as the obligation ends.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              7. Your Privacy Rights
            </Typography>
            <Typography as="p" className="mb-4">
              You have important rights regarding your personal information:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Access and Portability:</strong> You can access your information at any time
                through your account and request an export of your data.
              </li>
              <li>
                <strong>Correction:</strong> You can update or correct your information at any time
                through your account settings.
              </li>
              <li>
                <strong>Deletion:</strong> You can delete your data at any time, as described in
                Section 6.2.
              </li>
              <li>
                <strong>Restrict or Object to Processing:</strong> You can restrict processing by
                deleting your account or specific information, or contact us to discuss concerns at{" "}
                <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                  general@legacykeeper.app
                </a>
                .
              </li>
              <li>
                <strong>Withdraw Consent:</strong> Where we rely on your consent, you can withdraw
                it at any time by closing your account or deleting specific data.
              </li>
              <li>
                <strong>Lodge a Complaint:</strong> You have the right to lodge a complaint with
                your local data protection authority.
              </li>
            </ul>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              8. Special Privacy Considerations
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              8.1 Children's Privacy
            </Typography>
            <Typography as="p" className="mb-6">
              Legacy Keeper is intended only for adults aged 18 and older. We do not knowingly
              collect personal information from anyone under 18. If we learn that we have collected
              information from a minor, we will delete it promptly. If you believe a minor has
              provided us with personal information, please contact us at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
              .
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              8.2 Sensitive Personal Information
            </Typography>
            <Typography as="p" className="mb-6">
              Estate planning often involves sensitive personal information, including health
              information, financial details, and family relationships. We treat all of your
              information with the highest level of protection, applying the same strict security
              and privacy standards regardless of the sensitivity of specific data.
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              8.3 California Privacy Rights
            </Typography>
            <Typography as="p" className="mb-6">
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA), including the right to know what personal information we
              collect, use, and disclose; the right to request deletion; the right to opt out of the
              sale of personal information (we do not sell personal information); and the right to
              non-discrimination for exercising your privacy rights. To exercise these rights,
              contact us at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
              .
            </Typography>

            <Typography as="h3" size="xl" className="mt-8 mb-4">
              8.4 European (Global) Privacy Rights (GDPR)
            </Typography>
            <Typography as="p" className="mb-6">
              Legacy Keeper operates only in the United States and Canada. We do not meet the
              standards for GDPR compliance and do not offer GDPR rights to our users. If you are
              located in the European Union, we recommend that you do not use our Service.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              9. Changes to This Privacy Policy
            </Typography>
            <Typography as="p" className="mb-6">
              We may update this Privacy Policy from time to time. When we make material changes, we
              will post the updated policy, update the Effective Date, notify you via email at least
              30 days before the changes take effect, and provide a prominent notice within the
              Service. Your continued use of the Service after the effective date constitutes
              acceptance of the changes.
            </Typography>
          </section>

          <section>
            <Typography as="h2" size="2xl" className="mt-12 mb-6">
              10. Contact Us
            </Typography>
            <Typography as="p" className="mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy, please
              contact us:
            </Typography>
            <Typography as="p" className="mb-2 font-semibold">
              Traction Technologies Inc. / 1546045 B.C. LTD. (Legacy Keeper)
            </Typography>
            <Typography as="p" className="mb-2">
              Email:{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
            </Typography>
            <Typography as="p" className="mb-2">
              Website:{" "}
              <a href="https://legacykeeper.app" className="text-primary hover:underline">
                https://legacykeeper.app
              </a>
            </Typography>
            <Typography as="p" className="mb-6">
              Attention: Privacy Officer
            </Typography>
            <Typography as="p" className="mb-6">
              We will respond to your inquiry within 30 days.
            </Typography>
          </section>
        </article>
      </main>
    </div>
  )
}
